import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { registrarJanelaNoHistorico } from "@/lib/historico-janelas";

/** Fornece a função de fechamento quando a aba está rodando dentro de uma janela popup. */
const JanelaCtx = createContext<(() => void) | null>(null);

/** Metadados da janela atual (arquivo .tsx e título), para o painel de depuração. */
export type JanelaInfo = { arquivo?: string; titulo?: string };
const JanelaInfoCtx = createContext<JanelaInfo | null>(null);

export function JanelaProvider({
  aoFechar,
  arquivo,
  titulo,
  children,
}: {
  aoFechar: () => void;
  arquivo?: string;
  titulo?: string;
  children: ReactNode;
}) {
  const fecharRef = useRef(aoFechar);
  fecharRef.current = aoFechar;
  // Herda os metadados do pai quando esta janela não os informa, evitando que
  // um popup sem `arquivo` regrida o painel de depuração para a rota (ex.: inicio).
  const pai = useContext(JanelaInfoCtx);
  const info: JanelaInfo = { arquivo: arquivo ?? pai?.arquivo, titulo: titulo ?? pai?.titulo };

  useEffect(() => registrarJanelaNoHistorico(() => fecharRef.current()), []);

  // A página é renderizada encostada à esquerda; os botões de retorno/info são
  // agrupados ao título pela própria página via `<BotoesJanela />`.
  return (
    <JanelaCtx.Provider value={aoFechar}>
      <JanelaInfoCtx.Provider value={info}>{children}</JanelaInfoCtx.Provider>
    </JanelaCtx.Provider>
  );
}

/** Retorna a função de fechar a janela, ou null se a aba está na janela principal. */
export function useJanela() {
  return useContext(JanelaCtx);
}

/** Retorna os metadados (arquivo/título) da janela atual, ou null na principal. */
export function useJanelaInfo() {
  return useContext(JanelaInfoCtx);
}
